import React, { useEffect, useState, useCallback } from 'react';
import { DollarSign, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { logError } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRefundsTab = ({ adminToken }) => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRefunds = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/refunds`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRefunds(data || []);
      }
    } catch (err) {
      logError('Failed to load refunds', err);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) loadRefunds();
  }, [adminToken, loadRefunds]);

  const totalRefunded = refunds.reduce((sum, r) => sum + (Number(r.refund_amount) || 0), 0);

  return (
    <div data-testid="admin-refunds-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700 }}>Refunds</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Total: <strong style={{ color: '#dc2626' }}>${totalRefunded.toFixed(2)}</strong> across {refunds.length} refunds
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={loadRefunds}
            data-testid="refresh-refunds-btn"
            style={{ fontSize: '0.75rem' }}
          >
            <RefreshCw size={14} style={{ marginRight: '0.4rem' }} />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardContent style={{ padding: 0, overflowX: 'auto' }}>
          <div style={{ minWidth: '750px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <tr>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>Confirmation #</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>Customer</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>Refund Date</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>Amount</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>Note</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>Loading…</td></tr>
                )}
                {!loading && refunds.length === 0 && (
                  <tr><td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>No refunds yet</td></tr>
                )}
                {!loading && refunds.map((r) => (
                  <tr key={r.id || r.refund_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem', fontWeight: 600, fontSize: '0.875rem' }}>
                      #{r.confirmation_number}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.8rem' }}>
                      <div style={{ fontWeight: 600 }}>{r.customer_name || r.user_name || 'N/A'}</div>
                      <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>{r.user_email || r.customer_email}</div>
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
                      {r.refunded_at ? new Date(r.refunded_at).toLocaleString() : 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 700, color: '#dc2626', fontSize: '0.875rem' }}>
                      <DollarSign size={12} style={{ display: 'inline' }} />
                      {Number(r.refund_amount || 0).toFixed(2)}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#374151', maxWidth: '220px' }}>
                      {r.refund_note || <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '9999px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: r.booking_status === 'cancelled' ? '#fee2e2' : '#fef3c7',
                        color: r.booking_status === 'cancelled' ? '#991b1b' : '#92400e',
                      }}>
                        {r.booking_status === 'cancelled' ? 'Cancelled' : 'Active (refunded)'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminRefundsTab;
